/**
 * levels.js - Door Knock's eight levels.
 *
 * Phases 2, 3 and 4. The overlap with Bubble Pop (phases 2 and 3) is what makes
 * section 9.3 testable - the same LEVEL going Green in two games - and phase 4
 * is what makes section 9.2 testable in the other direction: a child arriving
 * from another game already past this game's first level.
 *
 * ---------------------------------------------------------------------------
 * ONE PLACE THE SPEC AND THE FRAMEWORK DISAGREE, AND HOW THIS GAME CHOSE
 *
 * Section 4.3 calls Phase 4 Level 1 "-ing introduction (putting apple)" - a
 * level that introduces ONE grammar element. But the framework gives every
 * Phase 4 level four boxes, one of which is B, Prepositions, and section 8.1
 * rule 1 says measure all of them. A level whose content is only "-ing"
 * therefore scores B false on every trial and paints a box red for a skill it
 * never asked the child for.
 *
 * So Phase 4 Level 1 here asks for "putting frog in" - the -ing form AND a
 * preposition - which is not quite the level intent the spec describes. The
 * alternative was a permanently red box. Recorded in
 * docs/audit/GAME-02-CONTRACT.md; it is the framework or the spec that has to
 * give, not each game guessing separately.
 */

export const DEFAULT_PACING = {
  hintAtMs: 4500,
  escalateAtMs: [8500, 12500],
  windowMs: 17000,
};

export const LEVELS = [
  // ---- Phase 2: who is behind the door -----------------------------------
  {
    id: 'dk-p2l1',
    phase: 2,
    level: 1,
    scene: 'street',
    note: 'The spec\'s "partial word shown". A door opens a crack; the child names who is behind it.',
    pool: ['frog', 'owl', 'wolf', 'mouse', 'snail', 'lamb'],
    rounds: 3,
    targetsPerRound: 1,
    doorsOnScreen: 2,
    nominalRung: 'partial_verbal',
    cueStyle: 'partial_word',
    pacing: { hintAtMs: 4500, escalateAtMs: [9000], windowMs: 15000 },
  },
  {
    id: 'dk-p2l2',
    phase: 2,
    level: 2,
    scene: 'street',
    note: 'First letter only, and the door is lit.',
    pool: ['hen', 'goat', 'frog', 'owl', 'lamb', 'snail'],
    rounds: 3,
    targetsPerRound: 1,
    doorsOnScreen: 3,
    nominalRung: 'partial_verbal',
    cueStyle: 'first_letter',
    pacing: { hintAtMs: 4500, escalateAtMs: [9000], windowMs: 15000 },
  },
  {
    id: 'dk-p2l3',
    phase: 2,
    level: 3,
    scene: 'night',
    note: 'Opens at the visual rung: the lit door and nothing else. The first '
        + 'level here where component C - produced independently - is reachable '
        + 'without the ladder having been walked.',
    pool: ['wolf', 'mouse', 'goat', 'hen', 'frog', 'owl'],
    rounds: 3,
    targetsPerRound: 1,
    doorsOnScreen: 3,
    nominalRung: 'visual',
    cueStyle: 'none',
    pacing: { hintAtMs: 4500, escalateAtMs: [8500, 12500], windowMs: 17000 },
  },

  // ---- Phase 3: ask for one ----------------------------------------------
  // "get" where Bubble Pop offers "put". The carrier set is the FRAMEWORK's
  // (section 6.4), not the game's, which is why the box means the same thing in
  // both games even though the words on screen differ.
  {
    id: 'dk-p3l1',
    phase: 3,
    level: 1,
    scene: 'street',
    note: 'The spec\'s "2 objects + want ___ frame".',
    pool: ['frog', 'owl', 'wolf', 'hen'],
    rounds: 3,
    targetsPerRound: 1,
    doorsOnScreen: 2,
    carriers: ['want'],
    nominalRung: 'visual',
    cueStyle: 'none',
    pacing: DEFAULT_PACING,
  },
  {
    id: 'dk-p3l2',
    phase: 3,
    level: 2,
    scene: 'garden',
    note: 'A second carrier, doing something different: "get" sends Pip to '
        + 'fetch, "want" brings them to the child.',
    pool: ['lamb', 'snail', 'goat', 'mouse'],
    rounds: 3,
    targetsPerRound: 1,
    doorsOnScreen: 2,
    carriers: ['get'],
    nominalRung: 'visual',
    cueStyle: 'none',
    pacing: DEFAULT_PACING,
  },
  {
    id: 'dk-p3l3',
    phase: 3,
    level: 3,
    scene: 'garden',
    note: 'The spec\'s "3 objects, verb options shown". Every carrier is legal '
        + 'and the child chooses - a mand the child did not choose is not a mand.',
    pool: ['frog', 'owl', 'goat', 'lamb', 'hen'],
    rounds: 3,
    targetsPerRound: 1,
    doorsOnScreen: 3,
    carriers: ['want', 'get', 'more'],
    nominalRung: 'visual',
    cueStyle: 'none',
    pacing: DEFAULT_PACING,
  },

  // ---- Phase 4: where does it go -----------------------------------------
  {
    id: 'dk-p4l1',
    phase: 4,
    level: 1,
    scene: 'garden',
    note: 'The spec calls this "-ing introduction". It also carries a '
        + 'Prepositions box, so the content has to elicit one - see the note at '
        + 'the head of this file.',
    pool: ['frog', 'lamb', 'mouse', 'snail'],
    rounds: 3,
    targetsPerRound: 1,
    doorsOnScreen: 2,
    verbs: ['putting'],
    places: ['in'],
    nominalRung: 'gestural',
    cueStyle: 'none',
    pacing: DEFAULT_PACING,
  },
  {
    id: 'dk-p4l2',
    phase: 4,
    level: 2,
    scene: 'street',
    note: 'The spec\'s preposition "on". Two places are legal now, so the '
        + 'preposition carries meaning instead of being a fixed part of a '
        + 'memorised string.',
    pool: ['owl', 'hen', 'goat', 'wolf'],
    rounds: 3,
    targetsPerRound: 1,
    doorsOnScreen: 3,
    verbs: ['putting'],
    places: ['on', 'in'],
    nominalRung: 'positional',
    cueStyle: 'none',
    pacing: DEFAULT_PACING,
  },
];

export const COVERAGE = {
  phasesAvailable: [2, 3, 4],
  levelsAvailable: LEVELS.map((l) => ({ phase: l.phase, level: l.level })),
};

export function getLevelContent(phase, level) {
  return LEVELS.find((l) => l.phase === phase && l.level === level) || null;
}

export function pacingFor(level) {
  return { ...DEFAULT_PACING, ...(level.pacing || {}) };
}

export default { LEVELS, COVERAGE, DEFAULT_PACING, getLevelContent, pacingFor };
